import { TriangleAlert } from 'lucide-react';
import type { ScanIntegrityWarning } from '@/types/api';

/**
 * A2F-832: per-feature scope-filter-independent banner shown on each Review
 * page when data_integrity_warnings are present for that feature.
 *
 * Key design constraints (BRIEF gotcha #9):
 *   - This banner is driven by the TOP-LEVEL warnings list, NOT the
 *     scope-filtered row set. It therefore stays visible even when every
 *     flagged finding (typically tier='inherited') is filtered out by the
 *     default "this submission" scope filter.
 *   - Returns null when warnings.length === 0 — no render, no DOM impact.
 *   - Renders ABOVE the results table, sibling of the scope filter controls.
 *
 * Props:
 *   warnings: ScanIntegrityWarning[] — already filtered to this feature
 *             (caller filters by featureId before passing in).
 */
export function DataIntegrityNotice({ warnings }: { warnings: ScanIntegrityWarning[] }) {
  if (warnings.length === 0) return null;

  // Collect distinct unresolved filenames across all warnings for display.
  const unresolvedFiles = Array.from(
    new Set(warnings.flatMap((w) => w.filenames)),
  );

  const hasNoDocsWarning = warnings.some((w) => w.reason === 'no_documents');
  const hasUnresolvedWarning = warnings.some((w) => w.reason === 'unresolved_filename');

  let reasonText: string;
  if (hasNoDocsWarning && hasUnresolvedWarning) {
    reasonText = 'missing document references and unresolvable filenames';
  } else if (hasNoDocsWarning) {
    reasonText = 'missing document references';
  } else {
    reasonText = 'unresolvable filenames';
  }

  return (
    <div className="flex items-start gap-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
      <TriangleAlert className="w-4 h-4 mt-0.5 shrink-0 text-amber-500" aria-hidden="true" />
      <div className="space-y-1">
        <p>
          <span className="font-medium">{warnings.length} finding{warnings.length !== 1 ? 's' : ''}</span>
          {' '}could not be confidently scoped to this submission due to {reasonText}.
          The system is working from imperfect scan data — tiers shown may be conservative.
        </p>
        {unresolvedFiles.length > 0 && (
          <p className="text-xs text-amber-700">
            Unresolvable file{unresolvedFiles.length !== 1 ? 's' : ''}:{' '}
            {unresolvedFiles.join(', ')}
          </p>
        )}
      </div>
    </div>
  );
}
